"use client";

import { useCallback, useRef, useState } from "react";

function PlayPauseIcon({ isPlaying }: { isPlaying: boolean }) {
  if (isPlaying) {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
  );
}

type Props = {
  src: string;
  label: string;
  layout?: "Layout A" | "Layout B";
};

/** Muted looping background video with a bottom-right play/pause control. */
export default function HeroAmbientVideo({
  src,
  label,
  layout = "Layout A",
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const shapeClass =
    layout === "Layout B" ? "rounded-[3px]" : "rounded-full";

  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  return (
    <>
      <video
        ref={videoRef}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        src={src}
        autoPlay
        muted
        loop
        playsInline
        aria-label={label}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePlayPause();
        }}
        aria-label={isPlaying ? "Pause video" : "Play video"}
        className={`pointer-events-auto absolute bottom-3 right-3 z-20 flex size-10 cursor-pointer items-center justify-center ${shapeClass} text-brand-primary shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white`}
        style={{
          background:
            "color-mix(in oklab, var(--site-white-color, #fff) 90%, transparent)",
        }}
      >
        <PlayPauseIcon isPlaying={isPlaying} />
      </button>
    </>
  );
}
